import React from 'react'
import {
  MissionAbandonedMessage,
  MissionIsCancelledMessage,
  MissionIsValidatedMessage,
} from '../../../missions/mission-messages'
import { createColumns } from '../utils'
import { Element, Icon } from 'react-bulma-components'
import { TrimTextIfLongerThan } from '../../../../utils/texts'
import { upperFirst } from 'lodash'
import { ChevronUpIcon, EyeIcon } from '@heroicons/react/outline'
import { Link } from 'react-router-dom'
import { AppUserRole } from '../../../../store/session'
import { ColumnGroupDefinition } from '../types'
import { ChevronDownIcon } from '@heroicons/react/solid'
import { HelpIcon, InfoIcon } from '../../../icons'
import { Service } from '../../../../api/services'

interface MissionDetailsColumnGroupProps {
  currentUserRole: AppUserRole
  minimalMode?: boolean
  setExcludeGroupKeys: (excludeGroupKeys: string[]) => void
  excludeGroupKeys: string[]
}

const missionDetailsColumnGroup = ({
  currentUserRole,
  minimalMode,
  setExcludeGroupKeys,
  excludeGroupKeys,
}: MissionDetailsColumnGroupProps): ColumnGroupDefinition => ({
  Header: 'Mission',
  id: 'mission.details',
  columns: createColumns([
    {
      header: currentUserRole === 'employerMember' ? 'Employeur' : 'Client',
      id: 'mission.employer',
      shouldMerge: true,
      render: ({ mission }) => (
        <Element renderAs={'span'} textColor="dark" textWeight="semibold">
          {currentUserRole === 'employerMember'
            ? (mission.interimAgency?.name ?? mission.employer.name)
            : mission.employer.name}
        </Element>
      ),
    },
    {
      header: 'Affectation',
      id: 'mission.service',
      isHidden: minimalMode,
      render: ({ mission }) => {
        const serviceNames: string[] = []
        mission.workPeriods.forEach(wp => {
          const serviceName = (wp.service as Service)?.name
          if (serviceName && !serviceNames.includes(serviceName)) {
            serviceNames.push(serviceName)
          }
        })
        const hasMultipleServices = serviceNames.length > 1
        return (
          <Element display="flex" alignItems="center">
            <Element renderAs={'span'} textColor="dark" textWeight="semibold">
              <TrimTextIfLongerThan ifIsLongerThan={20}>
                {upperFirst(mission.service.name)}
              </TrimTextIfLongerThan>
            </Element>
            {hasMultipleServices && (
              <Icon
                size="small"
                color="grey-light"
                data-tooltip={`Des journées de travail sont liées à plusieurs affectations : ${serviceNames.join(
                  ', ',
                )}`}
                ml={1}
              >
                <InfoIcon />
              </Icon>
            )}
          </Element>
        )
      },
    },
    {
      header: 'Métier',
      id: 'mission.jobTitle',
      isHidden: minimalMode,
      render: ({ mission }) => (
        <Element renderAs={'span'} textColor="dark" textWeight="semibold">
          <TrimTextIfLongerThan ifIsLongerThan={20}>
            {upperFirst(mission.jobTitle.name)}
          </TrimTextIfLongerThan>
        </Element>
      ),
    },
    {
      header: (
        <Icon data-tooltip={'Voir la mission'} className="has-tooltip-left" size="small">
          <EyeIcon />
        </Icon>
      ),
      id: 'mission.link',
      render: ({ mission, rowGroupOptions }) => {
        if (mission.groupedMissions.length > 1) {
          return (
            <Element
              renderAs={'span'}
              textColor="dark"
              textWeight="semibold"
              className="table-section-start"
              data-test="expand-summary-mission"
              onClick={() => setExcludeGroupKeys([...excludeGroupKeys, mission.groupedKey])}
            >
              <Icon
                data-tooltip={`${mission.groupedMissions.length} missions.\nCliquer pour afficher une ligne par mission`}
                className="has-tooltip-left"
                color="link"
              >
                {mission.groupedMissions.length}
                <ChevronDownIcon />
              </Icon>
            </Element>
          )
        }
        const isInExcludeGroupKeys = mission.groupedKey && mission.groupedKey.includes(mission._id) // TODO: improve this
        return (
          <>
            <Element
              renderAs={Link}
              to={`/missions/${mission._id}`}
              textWeight="bold"
              data-test="go-to-mission-details-action"
              mt={1}
            >
              <Icon data-tooltip={'Voir la mission'} className="has-tooltip-left" size="small">
                <EyeIcon />
              </Icon>
            </Element>
            {isInExcludeGroupKeys ? (
              <Element
                onClick={() =>
                  setExcludeGroupKeys(
                    excludeGroupKeys.filter(
                      key => key !== mission.groupedKey.replace(`_${mission._id}`, ''),
                    ),
                  )
                }
                renderAs={'span'}
                mt={1}
              >
                <Icon
                  data-tooltip={`Cette mission peut être regroupée avec d'autres missions.\nCliquer pour regrouper en une ligne`}
                  className="has-tooltip-left"
                  data-test="group-summary-mission"
                  color="link"
                  size="small"
                >
                  <ChevronUpIcon />
                </Icon>
              </Element>
            ) : null}
          </>
        )
      },
    },
    {
      header: (
        <MissionIsValidatedMessage
          mission={{ isValidated: true }}
          type="icon"
          small
          iconColor="dark"
        />
      ),
      id: 'mission.validation',
      isHidden: minimalMode,
      render: ({ mission }) => (
        <MissionIsValidatedMessage mission={mission} type="icon" small iconColor={'primary'} />
      ),
    },
    {
      header: (
        <MissionAbandonedMessage
          mission={{ isAbandoned: true }}
          type="icon"
          small
          iconColor="dark"
        />
      ),
      id: 'mission.abandoned',
      render: ({ mission }) => <MissionAbandonedMessage mission={mission} type="icon" small />,
    },
    {
      header: (
        <MissionIsCancelledMessage
          mission={{ isCancelled: true }}
          type="icon"
          small
          iconColor="dark"
        />
      ),
      id: 'mission.cancelled',
      render: ({ mission }) => <MissionIsCancelledMessage mission={mission} type="icon" small />,
    },
  ]),
})

export default missionDetailsColumnGroup
